<template>
  <stihl-modal class="modal" @modal-closed="$emit('cancel')">

    <div class="title-row">
      <h3 class="title">{{ $t('adm.page.changePwd.title') }}</h3>
    </div>

    <div class="modal-body">
      <form-item-group
        container-class="mb-4"
        :required="true"
        :label="$t('adm.page.changePwd.newPassword')"
        label-for="newPassword"
        :validation="v$.form.newPassword">
        <input
          id="newPassword"
          type="password"
          autocomplete="new-password"
          class="form-control"
          v-model="v$.form.newPassword.$model"
          aria-label="new password">
      </form-item-group>
      <form-item-group
        container-class="mb-4"
        :required="true"
        :label="$t('adm.page.changePwd.newPassword2')"
        label-for="newPassword2"
        :validation="v$.form.newPassword2">
        <input
          id="newPassword2"
          type="password"
          autocomplete="new-password"
          class="form-control"
          v-model="v$.form.newPassword2.$model"
          aria-label="repeat new password">
      </form-item-group>
      <hr />
      <form-item-group
        container-class="mb-4"
        :required="true"
        :label="$t('adm.page.changePwd.myPassword')"
        label-for="myPassword"
        :validation="v$.form.myPassword">
        <input
          id="myPassword"
          type="password"
          autocomplete="current-password"
          class="form-control"
          v-model="v$.form.myPassword.$model"
          aria-label="password">
      </form-item-group>
    </div>

    <div class="buttons">
      <button type="button" class="btn btn-default" data-bs-dismiss="modal">{{ $t('adm.button.cancel') }}</button>
      <button type="button" class="btn btn-secondary" @click.stop="save" :disabled="v$.$invalid">
        {{ $t('adm.button.save') }}
      </button>
    </div>

  </stihl-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import StihlModal from '@/components/ui/StihlModal.vue';
import FormItemGroup from '@/components/ui/FormItemGroup.vue';
import useVuelidate from '@vuelidate/core';
import {
  hasDigit,
  hasLowercase,
  hasSpecial,
  hasUppercase,
  invalidCharacters,
  maxLength,
  minLength,
  required,
} from '@/components/validators';
import { sameAs } from '@vuelidate/validators';
import Backend from '@/components/backend';
import { Logger } from 'oidc-client-ts';

export default defineComponent({
  name: 'ChangeUserPasswordModal',
  components: { FormItemGroup, StihlModal },
  emits: ['cancel', 'changed'],
  props: {
    uid: { type: String, required: true },
  },
  data() {
    return {
      form: {
        myPassword: '',
        newPassword: '',
        newPassword2: '',
      },
      vuelidateExternalResults: {
        form: {
          myPassword: [] as string[],
          newPassword: [] as string[],
        },
      },
    };
  },
  methods: {
    async save() {
      this.$toast.clear();

      const isValid = await this.v$.$validate();
      if (!isValid) {
        Logger.error('ChangePWD', 'validation failed', this.v$?.$errors);
        return;
      }

      Backend.validateToken().then((api) => api.changeUsersPassword(this.uid, {
        myPassword: this.form.myPassword,
        newPassword: this.form.newPassword,
      }).then(() => {
        Logger.error('ChangePWD', 'SUCCESS');
        this.$toast.success(this.$t('adm.page.changePwd.infoSuccess'));
        this.$emit('changed', true);
      }).catch((e) => {
        // Logger.error("ChangePWD", "ERROR", e.response)
        if (e.response.data?.code === 'INVALID_PARAMETER') {
          if (e.response.data?.invalidField === 'myPassword') {
            this.vuelidateExternalResults.form.myPassword = [this.$t('adm.page.changePwd.invalidPassword')];
            return;
          }
          if (e.response.data?.invalidField === 'newPassword') {
            this.vuelidateExternalResults.form.newPassword = [this.$t('adm.page.changePwd.invalidNewPassword')];
            return;
          }
        }
        this.$toast.error(this.$t('adm.error.generic'));
      }));
    },
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        myPassword: {
          required,
          // minLength: minLength(12),
          maxLength: maxLength(127),
          invalidCharacters,
        },
        newPassword: {
          required,
          minLength: minLength(12),
          maxLength: maxLength(127),
          hasLowercase,
          hasUppercase,
          hasDigit,
          hasSpecial: hasSpecial('! $ % & * - + = ; : , . " \' ? ~ # < > | § / @ ^ _ ( ) [ ] { }'),
          invalidCharacters,
        },
        newPassword2: {
          required,
          sameAsPassword: sameAs(this.form.newPassword),
        },
      },
    };
  },
});
</script>

<style>
</style>
